/**
 * 이메일 로그인 / 가입 — 1등급 앱 (테스트·일반 계정용)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoArrowBack } from 'react-icons/io5';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';

const ERR = {
  'auth/email-already-in-use': '이미 가입된 이메일이에요.',
  'auth/invalid-email': '이메일 형식이 올바르지 않아요.',
  'auth/weak-password': '비밀번호는 6자 이상이어야 해요.',
  'auth/invalid-credential': '이메일 또는 비밀번호가 틀렸어요.',
  'auth/user-not-found': '가입되지 않은 이메일이에요.',
  'auth/wrong-password': '비밀번호가 틀렸어요.',
};

const EmailAuthPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const signup = mode === 'signup';
  const valid = email.trim() && pw.length >= 6 && (!signup || name.trim());

  const submit = async () => {
    if (!valid || busy) return;
    setErr('');
    setBusy(true);
    try {
      if (signup) {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), pw);
        await setDoc(doc(db, 'users', cred.user.uid), {
          uid: cred.user.uid,
          name: name.trim(),
          email: email.trim(),
          role: 'parent',
          status: 'approved',
          provider: 'email',
          memberships: [],
          createdAt: serverTimestamp(),
        });
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), pw);
      }
      navigate('/app/home', { replace: true });
    } catch (e) {
      console.error('이메일 인증 실패:', e);
      setErr(ERR[e.code] || '처리에 실패했어요. 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap>
      <Top>
        <BackBtn type="button" onClick={() => navigate(-1)}><IoArrowBack size={22} /></BackBtn>
      </Top>

      <Content>
        <Title>{signup ? '이메일로 가입' : '이메일로 로그인'}</Title>
        <Sub>1등급을 이메일 계정으로 이용해요.</Sub>

        <Form>
          {signup && (
            <>
              <FLabel>이름</FLabel>
              <FInput value={name} onChange={(e) => setName(e.target.value)} />
            </>
          )}
          <FLabel>이메일</FLabel>
          <FInput type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <FLabel>비밀번호 (6자 이상)</FLabel>
          <FInput
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          {err && <ErrText>{err}</ErrText>}
        </Form>
      </Content>

      <Bottom>
        <SubmitBtn type="button" disabled={!valid || busy} onClick={submit}>
          {busy ? '처리 중...' : signup ? '가입하고 시작하기' : '로그인'}
        </SubmitBtn>
        <Toggle type="button" onClick={() => { setMode(signup ? 'login' : 'signup'); setErr(''); }}>
          {signup ? '이미 계정이 있어요 · 로그인' : '계정이 없어요 · 가입하기'}
        </Toggle>
      </Bottom>
    </Wrap>
  );
};

export default EmailAuthPage;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const Top = styled.div`
  padding: 14px 12px 0;
`;

const BackBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Content = styled.div`
  flex: 1;
  padding: 24px 22px;
`;

const Title = styled.p`
  font-size: 23px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 8px;
`;

const Form = styled.div`
  margin-top: 28px;
`;

const FLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 16px 0 7px;
`;

const FInput = styled.input`
  width: 100%;
  padding: 15px 16px;
  border: 0;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 16px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const ErrText = styled.p`
  margin-top: 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent.red};
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 24px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;

const Toggle = styled.button`
  background: none;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 4px 10px;
`;
